import {
  teamBillingSummary,
  type BillingPlan,
  type Team,
  type TeamUserRole,
  type UserRef,
} from '@scalar-org/entities/team'
import { z } from 'zod'

import type { RequestService } from '../request'
import { nanoidSchema } from '@scalar-org/entities/utility'

export function teamApiFactory(
  requestService: RequestService,
  getNewTokens: (teamUid?: string) => Promise<void>,
) {
  async function create(data: {
    teamInfo: {
      name: string
      slug: string
    }
    project?: Record<string, any>
    invites?: {
      email: string
      role?: TeamUserRole
    }[]
  }) {
    return await requestService
      .request({
        url: 'core/team/create',
        method: 'post',
        data,
        schema: z.object({
          teamUid: z.string(),
        }),
      })
      .then(async (r) => {
        if (!r.error) await getNewTokens(r.data.teamUid)

        return r
      })
  }

  async function inviteMember(data: { email: string; role: TeamUserRole }) {
    return await requestService.request({
      url: 'core/team/invite',
      method: 'post',
      data,
      schema: z.union([z.null(), z.string()]),
    })
  }

  async function removeInvite(invitedUserUid: string) {
    return await requestService.request({
      url: 'core/team/invite',
      method: 'delete',
      data: { uid: invitedUserUid },
      schema: z.null(),
    })
  }

  async function updateSlug(slug: string) {
    return await requestService.request({
      url: 'core/team/slug',
      method: 'post',
      data: { slug },
      schema: z.null(),
    })
  }

  async function checkSlug(slug: string) {
    return await requestService.request({
      url: 'core/team/slug/available',
      method: 'post',
      data: { slug },
      schema: z.object({
        isAvailable: z.boolean(),
      }),
    })
  }

  async function createNamespace(namespace: string) {
    return await requestService.request({
      url: 'core/team/namespace',
      method: 'post',
      data: { namespace },
      schema: z.null(),
    })
  }

  async function updateNamespace(namespace: string, newNamespace: string) {
    return await requestService.request({
      url: 'core/team/namespace/update',
      method: 'post',
      data: { namespace, newNamespace },
      schema: z.null(),
    })
  }

  async function checkNamespace(namespace: string) {
    return await requestService.request({
      url: 'core/team/namespace/available',
      method: 'post',
      data: { namespace },
      schema: z.object({
        isAvailable: z.boolean(),
      }),
    })
  }

  async function rename(name: string) {
    return await requestService.request({
      url: 'core/team/rename',
      method: 'post',
      data: { name },
      schema: z.null(),
    })
  }

  async function leave() {
    return await requestService
      .request({
        url: 'core/team/leave',
        method: 'get',
        schema: z.null(),
      })
      .then(async (r) => {
        if (!r.error) await getNewTokens()

        return r
      })
  }

  async function removeMember(userUid: string) {
    return await requestService.request({
      url: 'core/team/delete-member',
      method: 'post',
      data: {
        userUid,
      },
      schema: z.null(),
    })
  }

  async function updateRole(data: { userUid: string; role: TeamUserRole }) {
    return await requestService.request({
      url: 'core/team/update-role',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  async function deleteTeam() {
    return await requestService
      .request({
        url: 'core/team',
        method: 'delete',
        data: null,
        schema: z.object({
          newTeamUid: nanoidSchema.nullable(),
        }),
      })
      .then(async (r) => {
        if (!r.error) await getNewTokens()

        return r
      })
  }

  async function updateImage(imageUri: string) {
    return await requestService.request({
      url: 'core/team/image',
      method: 'post',
      data: {
        imageUri,
      },
      schema: z.null(),
    })
  }

  async function update(data: Partial<Pick<Team, 'theme' | 'icons'>>) {
    return await requestService.request({
      url: 'core/team/update',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  async function updateTeamUser(updateRequestData: {
    teamUser: Partial<Omit<UserRef, 'uid' | 'role'>>
    userUid: string
  }) {
    return await requestService.request({
      url: 'core/team/update-user',
      method: 'post',
      data: updateRequestData,
      schema: z.null(),
    })
  }

  async function getBillingSummary() {
    return await requestService.request({
      url: 'core/team/billing',
      method: 'get',
      schema: teamBillingSummary,
    })
  }

  async function updateBillingPlan(
    billingPlan:
      | BillingPlan.Free
      | BillingPlan.ProMonthly
      | BillingPlan.ProYearly,
  ) {
    return await requestService.request({
      url: 'core/team/update/plan',
      method: 'post',
      data: {
        billingPlan,
      },
      schema: z.null(),
    })
  }

  async function onboardingSetup(data: {
    slug: string
    name: string
    imageUri?: string
    invites?: {
      email: string
      role?: TeamUserRole
    }[]
  }) {
    return await requestService.request({
      url: 'core/team/onboarding/setup',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  return {
    create,
    inviteMember,
    removeInvite,
    rename,
    leave,
    removeMember,
    delete: deleteTeam,
    getBillingSummary,
    updateBillingPlan,
    update,
    updateRole,
    updateTeamUser,
    updateImage,
    updateSlug,
    checkSlug,
    createNamespace,
    updateNamespace,
    checkNamespace,
    onboardingSetup,
  }
}
